import { AxiosError } from "axios";
import { handleApiError } from "./instanceApi";
import { moodleInstanceApi } from "./moodleInstanceApi";
import qs from "qs"; // Thêm thư viện qs để chuyển đổi payload thành x-www-form-urlencoded
import { getSiteInfo } from "./moodleUserApi"; // Import getSiteInfo

const getMoodleRequestBody = (wsfunction: string, params: any = {}): string => {
  const wstoken = sessionStorage.getItem("wstoken");
  if (!wstoken) {
    throw new Error("No wstoken found in session storage");
  }
  const baseParams = {
    wsfunction,
    moodlewsrestformat: "json",
    wstoken,
  };
  return qs.stringify({ ...baseParams, ...params });
};

export const getAllQuiz = async () => {
  try {
    // Lấy thông tin người dùng
    const siteInfo = await getSiteInfo();
    const userid = siteInfo?.userid;

    if (!userid) {
      throw new Error("User ID not found from site info");
    }

    // Lấy danh sách các khóa học của người dùng
    const userCoursesRequestBody = getMoodleRequestBody("core_enrol_get_users_courses", {
      userid,
    });
    const userCoursesResponse = await moodleInstanceApi.post(
      "webservice/rest/server.php",
      userCoursesRequestBody,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );
    const courseIds = userCoursesResponse.data.map((course: { id: number }) => course.id);
    if (!courseIds.length) {
      throw new Error("No courses found for the user");
    }

    // Lấy danh sách quiz
    const quizRequestBody = getMoodleRequestBody("mod_quiz_get_quizzes_by_courses", {
      courseids: courseIds,
    });
    const quizResponse = await moodleInstanceApi.post(
      "webservice/rest/server.php",
      quizRequestBody,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );
    const quizzes = quizResponse.data.quizzes || [];

    // Với mỗi quiz, xử lý trạng thái và lấy câu hỏi
    const quizzesWithDetails = await Promise.all(
      quizzes.map(async (quiz: any) => {
        let attempts: any[] = [];
        let detailedAttempt: any = null;
        let questions: any[] = [];
        let status = 0;
        let questionCount = 0;

        // Lấy attempt hiện tại
        const attemptsBody = getMoodleRequestBody("mod_quiz_get_user_attempts", {
          quizid: quiz.id,
          userid,
          status: "all"
        });
        const attemptsRes = await moodleInstanceApi.post(
          "webservice/rest/server.php",
          attemptsBody,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        attempts = (attemptsRes.data.attempts || []).sort(
          (a: any, b: any) => b.timestart - a.timestart
        );

        if (attempts.length > 0) {
          const lastAttempt = attempts[0];

          if (lastAttempt.state === "finished" || lastAttempt.state === "overdue") {
            status = 1;
            const reviewBody = getMoodleRequestBody("mod_quiz_get_attempt_review", {
              attemptid: lastAttempt.id,
            });
            const reviewRes = await moodleInstanceApi.post(
              "webservice/rest/server.php",
              reviewBody,
              { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
            );
            detailedAttempt = reviewRes.data.attempt;
            questions = reviewRes.data.questions || [];
          } else if (lastAttempt.state === "inprogress") {
            status = 2;
            const allQuestions: any[] = [];
            let page = 0;
            let lastResponse: any = null;

            while (page !== -1) {
              const dataBody = getMoodleRequestBody("mod_quiz_get_attempt_data", {
                attemptid: lastAttempt.id,
                page: page,
              });
              const dataRes = await moodleInstanceApi.post(
                "webservice/rest/server.php",
                dataBody,
                { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
              );
              
              if (dataRes.data.questions && Array.isArray(dataRes.data.questions)) {
                allQuestions.push(...dataRes.data.questions);
              }
              
              page = dataRes.data.nextpage;
              lastResponse = dataRes;
            }

            detailedAttempt = lastResponse.data.attempt;
            questions = allQuestions;
          }
        }

        questionCount = questions.length;

        return {
          ...quiz,
          attempts,
          status,
          questionCount,
          detailedAttempt,
          questions,
        };
      })
    );

    return { quizzes: quizzesWithDetails };
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getQuizByCourseId = async (courseId: number) => {
  try {
    if (!courseId || isNaN(courseId)) {
      throw new Error("Invalid courseId provided");
    }
    const requestBody = getMoodleRequestBody("mod_quiz_get_quizzes_by_courses", {
      courseids: [courseId], // Sử dụng mảng với 1 phần tử
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getQuizDetails = async (quizId: number, userId: number = 0, courseId: number = 0) => {
  try {
    if (!quizId || isNaN(quizId)) {
      throw new Error("Invalid quizId provided");
    }

    const baseHeaders = {
      "Content-Type": "application/x-www-form-urlencoded",
    };

    // Get quiz name first
    let quizName = "";
    if (courseId) {
      const quizInfoRequest = getMoodleRequestBody("mod_quiz_get_quizzes_by_courses", {
        courseids: [courseId],
      });
      const quizInfoResponse = await moodleInstanceApi.post(
        "webservice/rest/server.php",
        quizInfoRequest,
        { headers: baseHeaders }
      );

      if (quizInfoResponse.data.exception) {
        return {
          type: "error",
          error: {
            exception: quizInfoResponse.data.exception,
            errorcode: quizInfoResponse.data.errorcode,
            message: quizInfoResponse.data.message
          }
        };
      }

      const quizInfo = quizInfoResponse.data.quizzes?.find((q: any) => q.id === quizId);
      if (quizInfo) {
        quizName = quizInfo.name;
      }
    }

    // Step 1: Try to get unfinished (in-progress) attempts
    const unfinishedRequest = getMoodleRequestBody("mod_quiz_get_user_attempts", {
      quizid: quizId,
      userid: userId,
      status: "unfinished", // This covers inprogress + not submitted
    });

    const unfinishedResponse = await moodleInstanceApi.post(
      "webservice/rest/server.php",
      unfinishedRequest,
      { headers: baseHeaders }
    );

    // Check for error response
    if (unfinishedResponse.data.exception) {
      return {
        type: "error",
        error: {
          exception: unfinishedResponse.data.exception,
          errorcode: unfinishedResponse.data.errorcode,
          message: unfinishedResponse.data.message
        }
      };
    }

    const unfinishedAttempts = unfinishedResponse.data.attempts || [];
    if (unfinishedAttempts.length > 0) {
      const attemptId = unfinishedAttempts[0].id;

      // Fetch all questions using pagination
      const allQuestions: any[] = [];
      let page = 0;
      let lastResponse: any = null;

      while (page !== -1) {
        const attemptDataRequest = getMoodleRequestBody("mod_quiz_get_attempt_data", {
          attemptid: attemptId,
          page: page,
        });

        const attemptDataResponse = await moodleInstanceApi.post(
          "webservice/rest/server.php",
          attemptDataRequest,
          { headers: baseHeaders }
        );

        // Check for error response
        if (attemptDataResponse.data.exception) {
          return {
            type: "error",
            error: {
              exception: attemptDataResponse.data.exception,
              errorcode: attemptDataResponse.data.errorcode,
              message: attemptDataResponse.data.message
            }
          };
        }

        if (attemptDataResponse.data.questions && Array.isArray(attemptDataResponse.data.questions)) {
          allQuestions.push(...attemptDataResponse.data.questions);
        }

        page = attemptDataResponse.data.nextpage;
        lastResponse = attemptDataResponse;
      }

      return {
        type: "inprogress",
        attemptId,
        data: {
          ...lastResponse.data,
          questions: allQuestions,
          quiz: { name: quizName }
        }
      };
    }

    // Step 2: Check for finished attempts
    const finishedRequest = getMoodleRequestBody("mod_quiz_get_user_attempts", {
      quizid: quizId,
      userid: userId,
      status: "finished",
    });

    const finishedResponse = await moodleInstanceApi.post(
      "webservice/rest/server.php",
      finishedRequest,
      { headers: baseHeaders }
    );

    // Check for error response
    if (finishedResponse.data.exception) {
      return {
        type: "error",
        error: {
          exception: finishedResponse.data.exception,
          errorcode: finishedResponse.data.errorcode,
          message: finishedResponse.data.message
        }
      };
    }

    const finishedAttempts = finishedResponse.data.attempts || [];
    if (finishedAttempts.length > 0) {
      const attemptId = finishedAttempts[0].id;

      const reviewRequest = getMoodleRequestBody("mod_quiz_get_attempt_review", {
        attemptid: attemptId,
      });

      const reviewResponse = await moodleInstanceApi.post(
        "webservice/rest/server.php",
        reviewRequest,
        { headers: baseHeaders }
      );

      // Check for error response
      if (reviewResponse.data.exception) {
        return {
          type: "error",
          error: {
            exception: reviewResponse.data.exception,
            errorcode: reviewResponse.data.errorcode,
            message: reviewResponse.data.message
          }
        };
      }

      return {
        type: "finished",
        attemptId,
        data: {
          ...reviewResponse.data,
          quiz: { name: quizName }
        }
      };
    }

    // Step 3: No attempt found
    return {
      type: "no_attempt",
      message: "No attempts found for this quiz",
    };

  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getAllAssign = async () => {
  try {
    // Lấy site info để có userid
    const siteInfo = await getSiteInfo();
    const userid = siteInfo?.userid;

    if (!userid) {
      throw new Error("User ID not found from site info");
    }

    // Bước 1: Lấy danh sách các khóa học mà người dùng đã tham gia
    const userCoursesRequestBody = getMoodleRequestBody("core_enrol_get_users_courses", {
      userid: userid
    });
    const userCoursesResponse = await moodleInstanceApi.post("webservice/rest/server.php", userCoursesRequestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    const courseIds = userCoursesResponse.data.map((course: { id: number }) => course.id);
    if (!courseIds.length) {
      // If no courses, return an empty array of assignments
      return [];
    }

    // Bước 2: Lấy tất cả bài tập từ các khóa học của người dùng
    const assignRequestBody = getMoodleRequestBody("mod_assign_get_assignments", {
      courseids: courseIds, // Sử dụng mảng courseIds từ userCourses
    });
    const assignResponse = await moodleInstanceApi.post("webservice/rest/server.php", assignRequestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    return assignResponse.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getAssignByCourseId = async (courseId: number) => {
  try {
    if (!courseId || isNaN(courseId)) {
      throw new Error("Invalid courseId provided");
    }
    const requestBody = getMoodleRequestBody("mod_assign_get_assignments", {
      courseids: [courseId],
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getAssignDetails = async (assignId: number) => {
  try {
    if (!assignId || isNaN(assignId)) {
      throw new Error("Invalid assignId provided");
    }
    const requestBody = getMoodleRequestBody("mod_assign_get_assignment", {
      assignmentids: [assignId], // Sửa thành assignmentids để phù hợp với tài liệu API
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

// For Quizzes
export const submitQuizAnswers = async (attemptId: number, data: { name: string; value: string }[], finishAttempt = false) => {
  try {
    if (!attemptId || isNaN(attemptId)) {
      throw new Error("Invalid attemptId provided");
    }

    const requestBody = getMoodleRequestBody("mod_quiz_process_attempt", {
      attemptid: attemptId,
      data: data,
      finishattempt: finishAttempt ? 1 : 0, // 1 để nộp bài, 0 để lưu tạm
    });

    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const saveIndividualQuizAnswer = async (attemptId: number, answerData: { name: string; value: string }) => {
  try {
    if (!attemptId || isNaN(attemptId)) {
      throw new Error("Invalid attemptId provided");
    }

    const requestBody = getMoodleRequestBody("mod_quiz_save_attempt", {
      attemptid: attemptId,
      data: [
        {
          name: answerData.name,
          value: answerData.value
        }
      ]
    });

    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const getStudentQuizAnswers = async (attemptId: number) => {
  try {
    if (!attemptId || isNaN(attemptId)) {
      throw new Error("Invalid attemptId provided");
    }
    const requestBody = getMoodleRequestBody("mod_quiz_get_attempt_review", {
      attemptid: attemptId,
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

// For Assignments
export const downloadAssignmentFile = async (fileId: number) => {
  try {
    if (!fileId || isNaN(fileId)) {
      throw new Error("Invalid fileId provided");
    }
    const wstoken = sessionStorage.getItem("wstoken");
    if (!wstoken) {
      throw new Error("No wstoken found in session storage");
    }
    const response = await moodleInstanceApi.get(`webservice/pluginfile.php`, {
      params: {
        fileid: fileId, // Thay 'file' bằng 'fileid' nếu đúng theo cấu hình Moodle
        token: wstoken,
      },
      responseType: "blob", // Để xử lý file download
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const submitAssignment = async (assignId: number, submissionData: any) => {
  try {
    if (!assignId || isNaN(assignId)) {
      throw new Error("Invalid assignId provided");
    }
    const requestBody = getMoodleRequestBody("mod_assign_save_submission", {
      assignmentid: assignId,
      plugindata: JSON.stringify(submissionData), // Chuyển submissionData thành chuỗi JSON
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const updateAssignment = async (assignId: number, submissionData: any) => {
  try {
    if (!assignId || isNaN(assignId)) {
      throw new Error("Invalid assignId provided");
    }
    const requestBody = getMoodleRequestBody("mod_assign_save_submission", {
      assignmentid: assignId,
      plugindata: JSON.stringify(submissionData),
      action: "update", // Thêm tham số để chỉ định cập nhật (nếu cần)
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};

export const deleteAssignmentFile = async (assignId: number, fileId: number) => {
  try {
    if (!assignId || !fileId || isNaN(assignId) || isNaN(fileId)) {
      throw new Error("Invalid assignId or fileId provided");
    }
    const requestBody = getMoodleRequestBody("mod_assign_delete_submission_file", {
      assignmentid: assignId,
      fileid: fileId,
    });
    const response = await moodleInstanceApi.post("webservice/rest/server.php", requestBody, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError);
  }
};